import React, { useState, useEffect } from 'react';
import { Grid, TextField, Button, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BaseModal from '../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { formatPhoneNumberForSubmission } from '../../../../components/utils/FormInputs/PhoneNumberInput';
import { USAbbreviations } from '../../../../utils/select_helpers/UnitedStatesAbbreviations';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { MuiTelInput } from 'mui-tel-input';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useGooglePlacesAutocomplete } from '../../../../components/address_autocomplete/useGooglePlacesAutoComplete';
import { validateZipCode } from '../../../../utils/validation/ZipCodeValidation';
import CustomAddressAutocomplete from '../../../../components/address_autocomplete/CustomAddressAutocomplete';

const AddCustomerModal = ({ show, onHide, accountId }) => {
    const { address, city, state, zipCode, addressPredictions, handleAddressInputChange, handleAddressSelect, setAddress, setCity, setState, setZipCode } = useGooglePlacesAutocomplete(show);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [clientEmail, setClientEmail] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { showToast } = useToast();
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [zipCodeError, setZipCodeError] = useState(false);
    const { t } = useTranslation();

    // Add validation state variables
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [stateError, setStateError] = useState(false);

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleZipCodeChange = (e) => {
        const newZip = e.target.value;
        setZipCodeError(!validateZipCode(newZip));
        setZipCode(newZip);
    };

    const handleSubmit = async () => {
        // Reset all error states
        setFirstNameError(false);
        setLastNameError(false);
        setPhoneNumberError(false);
        setEmailError(false);
        setAddressError(false);
        setCityError(false);
        setStateError(false);
        setZipCodeError(false);

        // Validate all fields
        let hasError = false;

        if (!firstName.trim()) {
            setFirstNameError(true);
            hasError = true;
        }
        if (!lastName.trim()) {
            setLastNameError(true);
            hasError = true;
        }
        if (!phoneNumber) {
            setPhoneNumberError(true);
            hasError = true;
        }
        if (!clientEmail.trim() || !validateEmail(clientEmail)) {
            setEmailError(true);
            hasError = true;
        }


        if (hasError) {
            return;
        }

        setLoadingData(true);
        const item = {
            address,
            city,
            state,
            email: clientEmail,
            zip_code: zipCode,
            first_name: firstName,
            last_name: lastName,
            phone_number: formatPhoneNumberForSubmission(phoneNumber),
            accountId: accountId
        };

        try {
            const functions = getFunctions();
            const createCustomer = httpsCallable(functions, CloudFunctions.CreateConnectedStripeAccountCustomer);
            const result = await createCustomer({ organizationId: firebaseUser.organization, accountId: accountId, customer: item });

            if (result.data.success) {
                showToast(t(StringKeys.SUCCESSFULLY_ADDED_X, {x:`${firstName} ${lastName}`}), ToastType.SUCCESS);
            } else {
                console.error('Error creating customer:', result.data.error);
                showToast(t(StringKeys.FAILED_TO_CREATE_X_PLEASE_TRY_AGAIN, {x: `${firstName} ${lastName}`}), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error calling createStripeCustomer function:', error);
            showToast(t(StringKeys.FAILED_TO_CREATE_X_PLEASE_TRY_AGAIN, {x: `${firstName} ${lastName}`}), ToastType.DANGER);
        }

        resetForm();
        setLoadingData(false);
        onHide();
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setClientEmail("");
        setCity("");
        setState("");
        setAddress("");
        setZipCode("");
        setZipCodeError(false);
        setFirstNameError(false);
        setLastNameError(false);
        setPhoneNumberError(false);
        setEmailError(false);
        setAddressError(false);
        setCityError(false);
        setStateError(false);
    };

    const handlePhoneNumberChange = (newValue) => {
        setPhoneNumber(newValue);
    };

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.ADD_CUSTOMER)}
            actions={actions}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="firstName"
                            label={t(StringKeys.FIRST_NAME)}
                            type="text"
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            error={firstNameError}
                            helperText={firstNameError ? t(StringKeys.FIRST_NAME_IS_REQUIRED) : ""}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            margin="dense"
                            id="lastName"
                            label={t(StringKeys.LAST_NAME)}
                            type="text"
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            error={lastNameError}
                            helperText={lastNameError ? t(StringKeys.LAST_NAME_IS_REQUIRED) : ""}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ marginTop: "8px" }}>
                        <MuiTelInput
                            label={t(StringKeys.PHONE)}
                            defaultCountry='US'
                            fullWidth
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            error={phoneNumberError}
                            helperText={phoneNumberError ? t(StringKeys.PHONE_NUMBER_IS_REQUIRED) : ""}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            margin="dense"
                            id="email"
                            label={t(StringKeys.EMAIL)}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                            error={emailError}
                            helperText={emailError ? t(StringKeys.EMAIL_IS_REQUIRED) : ""}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomAddressAutocomplete
                            value={address}
                            onChange={handleAddressInputChange}
                            onSelect={(selectedOption) => {
                                if (selectedOption) {
                                    handleAddressSelect(null, selectedOption);
                                }
                            }}
                            options={addressPredictions}
                            label={t(StringKeys.ADDRESS)}
                            required={true}
                            error={addressError}
                            helperText={addressError ? t(StringKeys.ADDRESS_IS_REQUIRED) : ""}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            margin="dense"
                            id="city"
                            label={t(StringKeys.CITY)}
                            type="text"
                            fullWidth
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            error={cityError}
                            helperText={cityError ? t(StringKeys.CITY_IS_REQUIRED) : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            margin="dense"
                            id="state"
                            label={t(StringKeys.STATE)}
                            fullWidth
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            variant="outlined"
                            error={stateError}
                            helperText={stateError ? t(StringKeys.STATE_IS_REQUIRED) : ""}
                        >
                            {USAbbreviations.map((abbreviation) => (
                                <MenuItem key={abbreviation} value={abbreviation}>
                                    {abbreviation}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            margin="dense"
                            id="zipCode"
                            label={t(StringKeys.ZIP_CODE)}
                            type="text"
                            fullWidth
                            value={zipCode}
                            onChange={handleZipCodeChange}
                            error={zipCodeError}
                            helperText={zipCodeError ? t(StringKeys.PLEASE_ENTER_A_VALID_ZIP_CODE) : ""}
                        />
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </BaseModal>
    );
};

export default AddCustomerModal;